import { writable, derived } from 'svelte/store';
import type { FlowNode, FlowEdge } from '../components/Flows/types/flow-types';
import { omitLegacyDragHandle } from '../components/Flows/types/flow-types';
import { validateAllNodes } from '../components/Flows/helpers/nodeValidation';
import { sanitizeFlowNodesAndEdges } from '../components/Flows/helpers/flowGraphSanitize';
import { generateNodeLabel } from '../components/Flows/PropertyEditors/helpers/LabelGenerator';
import {
	readFlowSelectionClipboard,
	writeFlowSelectionClipboard,
} from '../components/Flows/helpers/flowSelectionClipboard';

export interface FlowSceneMetadata {
	name: string;
	room: number | string;
	roomName: string;
	image?: string;
	backgroundColor?: string;
	activeTimeIntervals?: any[];
}

export const sceneMetadata = writable<FlowSceneMetadata>({
	name: 'Untitled Scene',
	room: 0,
	roomName: '',
	image: 'fa-diagram-project',
	backgroundColor: 'rgb(102, 96, 237)',
	activeTimeIntervals: [],
});

/** Canvas-only appearance inside the flow editor (nodes read via subscription). */
export type FlowCanvasTheme = 'dark' | 'light';

export const flowCanvasTheme = writable<FlowCanvasTheme>('dark');

export function toggleFlowCanvasTheme(): void {
	flowCanvasTheme.update((t) => (t === 'dark' ? 'light' : 'dark'));
}

// ---- History ring buffer ----
// Fixed-size circular buffer avoids O(N) array.shift() on every overflow.

const MAX_HISTORY = 50;

interface HistorySnapshot {
	nodes: FlowNode[];
	edges: FlowEdge[];
}

interface RingBuffer {
	slots: (HistorySnapshot | undefined)[];
	head: number; // index of the oldest entry
	size: number; // number of valid entries (≤ MAX_HISTORY)
}

function createRingBuffer(): RingBuffer {
	return { slots: new Array(MAX_HISTORY), head: 0, size: 0 };
}

/** Push a snapshot onto the ring buffer, evicting the oldest entry when full. */
function ringPush(rb: RingBuffer, snapshot: HistorySnapshot): RingBuffer {
	const next = { ...rb };
	if (next.size === MAX_HISTORY) {
		// Overwrite oldest (at head) and advance head
		const writeAt = next.head;
		next.slots = [...next.slots];
		next.slots[writeAt] = snapshot;
		next.head = (next.head + 1) % MAX_HISTORY;
	} else {
		const writeAt = (next.head + next.size) % MAX_HISTORY;
		next.slots = [...next.slots];
		next.slots[writeAt] = snapshot;
		next.size += 1;
	}
	return next;
}

/** Get snapshot at logical index i (0 = oldest). Returns undefined if out of range. */
function ringGet(rb: RingBuffer, i: number): HistorySnapshot | undefined {
	if (i < 0 || i >= rb.size) return undefined;
	return rb.slots[(rb.head + i) % MAX_HISTORY];
}

/** Truncate ring buffer to first `len` entries. */
function ringTruncate(rb: RingBuffer, len: number): RingBuffer {
	if (len >= rb.size) return rb;
	return { ...rb, size: Math.max(0, len) };
}

// ---- Store state ----

interface FlowEditorState {
	/** Primary node for property panel (last clicked / focused). */
	selectedNodeId: string | null;
	/** Multi-selection (includes primary). */
	selectedNodeIds: string[];
	nodes: FlowNode[];
	edges: FlowEdge[];
	isDirty: boolean;
	validationErrors: Map<string, string[]>;
	propertyChanges?: any;
	isEditorOpen?: boolean;
	clipboard: { nodes: FlowNode[]; edges: FlowEdge[] } | null;
	pendingNode: FlowNode | null;
	originalNodeForRevert: FlowNode | null;
	/** Ring buffer holding undo/redo snapshots */
	_history: RingBuffer;
	/** Logical cursor within the ring buffer (points to current state's predecessor) */
	historyIndex: number;
	/** True when validation has been scheduled but not yet applied */
	_validationPending: boolean;
	/** Dry-run / test-flow path highlight (node + edge ids). */
	simulationHighlight: { nodeIds: string[]; edgeIds: string[] } | null;
}

let nodeIdCounter = 0;
export function generateUniqueNodeId(): string {
	if (typeof crypto !== 'undefined' && crypto.randomUUID) {
		return `node-${crypto.randomUUID()}`;
	}
	++nodeIdCounter;
	return `node-${Date.now()}-${nodeIdCounter}-${Math.random().toString(36).slice(2, 9)}`;
}

// Safe deep clone — uses structuredClone (faster than JSON round-trip) when available.
function deepClone<T>(value: T): T {
	if (typeof structuredClone === 'function') return structuredClone(value);
	return JSON.parse(JSON.stringify(value));
}

function createFlowEditorStore() {
	const initialState: FlowEditorState = {
		selectedNodeId: null,
		selectedNodeIds: [],
		nodes: [],
		edges: [],
		isDirty: false,
		validationErrors: new Map(),
		propertyChanges: null,
		isEditorOpen: false,
		clipboard: null,
		pendingNode: null,
		originalNodeForRevert: null,
		_history: createRingBuffer(),
		historyIndex: -1,
		_validationPending: false,
		simulationHighlight: null,
	};

	const { subscribe, set: rawSet, update: rawUpdate } = writable<FlowEditorState>(initialState);

	/** Drop duplicate node ids / fix edge ids so Svelte keyed {#each} never throws; prune selection. */
	function withSanitizedGraph(state: FlowEditorState): FlowEditorState {
		const r = sanitizeFlowNodesAndEdges(state.nodes, state.edges, {
			logWarnings: true,
		});
		const validIds = new Set(r.nodes.map((n) => String(n.id)));
		let selectedNodeIds = [
			...new Set(state.selectedNodeIds.filter((id) => validIds.has(String(id)))),
		];
		let selectedNodeId = state.selectedNodeId;
		if (!selectedNodeId || !validIds.has(String(selectedNodeId))) {
			selectedNodeId = selectedNodeIds.length ? selectedNodeIds[selectedNodeIds.length - 1] : null;
		}
		if (selectedNodeId && !selectedNodeIds.includes(selectedNodeId)) {
			selectedNodeIds = [...selectedNodeIds, selectedNodeId];
		}
		return {
			...state,
			nodes: r.nodes,
			edges: r.edges,
			selectedNodeIds,
			selectedNodeId,
		};
	}

	function set(value: FlowEditorState) {
		rawSet(withSanitizedGraph(value));
	}

	function update(fn: (state: FlowEditorState) => FlowEditorState) {
		rawUpdate((state) => withSanitizedGraph(fn(state)));
	}

	// ---- History helpers ----

	function pushHistory(state: FlowEditorState): FlowEditorState {
		const snapshot: HistorySnapshot = {
			nodes: state.nodes.map((n) => deepClone(n)),
			edges: state.edges.map((e) => deepClone(e)),
		};
		// Truncate any redo entries above the current cursor before pushing
		const truncated = ringTruncate(state._history, state.historyIndex + 1);
		const nextRing = ringPush(truncated, snapshot);
		return {
			...state,
			_history: nextRing,
			historyIndex: nextRing.size - 1,
		};
	}

	// ---- Validation ----

	/**
	 * Apply node validation synchronously.
	 * Call directly only when you need an immediate result (e.g. on save).
	 * For mutations, prefer scheduleValidation() to batch rapid updates.
	 */
	function applyValidation(state: FlowEditorState): FlowEditorState {
		const results = validateAllNodes(state.nodes, state.edges);
		const nodesWithValidation = state.nodes.map((n) => {
			const r = results.get(n.id);
			return omitLegacyDragHandle({
				...n,
				validated: r?.isValid ?? true,
				errors: r?.errors ?? [],
			});
		});
		return {
			...state,
			nodes: nodesWithValidation,
			_validationPending: false,
		};
	}

	/**
	 * Schedule a deferred validation pass via microtask.
	 * Multiple rapid mutations will coalesce into a single validation run.
	 */
	function scheduleValidation(state: FlowEditorState): FlowEditorState {
		if (state._validationPending) return state;
		queueMicrotask(() => {
			update((s) => (s._validationPending ? applyValidation(s) : s));
		});
		return { ...state, _validationPending: true };
	}

	return {
		subscribe,
		set,
		update,

		selectNode: (nodeId: string | null, opts?: { additive?: boolean }) => {
			update((state) => {
				if (nodeId == null) {
					return {
						...state,
						selectedNodeId: null,
						selectedNodeIds: [],
					};
				}
				if (opts?.additive) {
					const set = new Set(state.selectedNodeIds);
					if (set.has(nodeId)) set.delete(nodeId);
					else set.add(nodeId);
					const next = [...set];
					return {
						...state,
						selectedNodeIds: next,
						selectedNodeId: nodeId,
					};
				}
				return {
					...state,
					selectedNodeId: nodeId,
					selectedNodeIds: [nodeId],
				};
			});
		},

		setSelectedNodeIds: (ids: string[]) => {
			update((state) => {
				const uniq = [...new Set(ids.filter(Boolean))];
				return {
					...state,
					selectedNodeIds: uniq,
					selectedNodeId: uniq.length ? uniq[uniq.length - 1] : null,
				};
			});
		},

		clearSimulationHighlight: () => {
			update((state) => ({ ...state, simulationHighlight: null }));
		},

		setSimulationHighlight: (nodeIds: string[], edgeIds: string[]) => {
			update((state) => ({
				...state,
				simulationHighlight: {
					nodeIds: [...new Set(nodeIds)],
					edgeIds: [...new Set(edgeIds)],
				},
			}));
		},

		addNode: (node: FlowNode) => {
			update((state) => {
				const withHistory = pushHistory(state);
				const newNode = omitLegacyDragHandle({
					...node,
					validated: node.validated ?? true,
					errors: node.errors ?? [],
				});
				return scheduleValidation({
					...withHistory,
					nodes: [...withHistory.nodes, newNode],
					isDirty: true,
				});
			});
			queueMicrotask(() => flushLiveFlowCanvasSync());
		},

		/** Add a connected template as one undoable operation. */
		addGraph: (newNodes: FlowNode[], newEdges: FlowEdge[]) => {
			update((state) => {
				const withHistory = pushHistory(state);
				return scheduleValidation({
					...withHistory,
					nodes: [...withHistory.nodes, ...newNodes.map(omitLegacyDragHandle)],
					edges: [...withHistory.edges, ...newEdges],
					isDirty: true,
				});
			});
			queueMicrotask(() => flushLiveFlowCanvasSync());
		},

		mutateGraph: (
			mutator: (nodes: FlowNode[], edges: FlowEdge[]) => { nodes: FlowNode[]; edges: FlowEdge[] },
		) => {
			update((state) => {
				const withHistory = pushHistory(state);
				const next = mutator(withHistory.nodes, withHistory.edges);
				return scheduleValidation({
					...withHistory,
					nodes: next.nodes,
					edges: next.edges,
					isDirty: true,
				});
			});
			queueMicrotask(() => flushLiveFlowCanvasSync());
		},

		duplicateNode: (nodeId: string) => {
			update((state) => {
				const original = state.nodes.find((n) => n.id === nodeId);
				if (!original) return state;
				const withHistory = pushHistory(state);
				let cloned: FlowNode = {
					...original,
					id: generateUniqueNodeId(),
					position: {
						x: original.position.x + 40,
						y: original.position.y + 40,
					},
					selected: false as any,
				};
				if (!cloned.data?.isLabelManuallyOverridden) {
					cloned = {
						...cloned,
						data: {
							...cloned.data,
							displayLabel: generateNodeLabel(cloned),
						},
					};
				}
				cloned = omitLegacyDragHandle(cloned);
				return scheduleValidation({
					...withHistory,
					nodes: [
						...withHistory.nodes.map((n) => ({
							...n,
							selected: false as any,
						})),
						cloned,
					],
					selectedNodeId: null,
					selectedNodeIds: [],
					isEditorOpen: false,
					isDirty: true,
				});
			});
		},

		updateNode: (nodeId: string, data: Partial<FlowNode>) => {
			update((state) => {
				const merged = state.nodes.find((n) => n.id === nodeId);
				if (!merged) return state;

				const { data: incomingData, ...topLevelPatch } = data;

				let updated: FlowNode = {
					...merged,
					...topLevelPatch,
					data:
						incomingData !== undefined
							? { ...(merged.data || {}), ...incomingData }
							: merged.data,
				};

				if (!updated.data?.isLabelManuallyOverridden) {
					updated = {
						...updated,
						data: {
							...updated.data,
							displayLabel: generateNodeLabel(updated),
						},
					};
				}

				updated = omitLegacyDragHandle(updated);
				const nodes = state.nodes.map((node) => (node.id === nodeId ? updated : node));
				const isPositionOnly = Object.keys(data).length === 1 && 'position' in data;
				const withHistory = isPositionOnly ? state : pushHistory(state);

				let pendingNode = state.pendingNode;
				if (state.isEditorOpen && state.pendingNode?.id === nodeId) {
					const pn = state.pendingNode;
					pendingNode = {
						...pn,
						...topLevelPatch,
						data:
							incomingData !== undefined
								? { ...(pn.data || {}), ...incomingData }
								: pn.data,
					};
				}

				const nextState = {
					...withHistory,
					nodes,
					pendingNode,
					isDirty: true,
				};
				// Position-only drags don't dirty validation
				return isPositionOnly ? nextState : scheduleValidation(nextState);
			});
		},

		removeNode: (nodeId: string) => {
			update((state) => {
				const withHistory = pushHistory(state);
				const nextIds = state.selectedNodeIds.filter((id) => id !== nodeId);
				const nextPrimary =
					state.selectedNodeId === nodeId
						? nextIds.length
							? nextIds[nextIds.length - 1]
							: null
						: state.selectedNodeId;
				const nextState = {
					...withHistory,
					nodes: withHistory.nodes.filter((node) => node.id !== nodeId),
					edges: withHistory.edges.filter(
						(edge) => edge.source !== nodeId && edge.target !== nodeId,
					),
					selectedNodeId: nextPrimary,
					selectedNodeIds: nextIds,
					isDirty: true,
				};
				return scheduleValidation(nextState);
			});
		},

		removeSelectedNodes: () => {
			update((state) => {
				const ids = state.selectedNodeIds;
				if (!ids.length) return state;
				const idSet = new Set(ids);
				const withHistory = pushHistory(state);
				return scheduleValidation({
					...withHistory,
					nodes: withHistory.nodes.filter((node) => !idSet.has(node.id)),
					edges: withHistory.edges.filter(
						(edge) => !idSet.has(edge.source) && !idSet.has(edge.target),
					),
					selectedNodeId: null,
					selectedNodeIds: [],
					isDirty: true,
				});
			});
		},

		addEdge: (edge: FlowEdge) => {
			update((state) => {
				// Skip if an identical edge ID already exists (prevents double-fire duplicates)
				if (state.edges.some((e) => e.id === edge.id)) return state;
				const withHistory = pushHistory(state);
				return scheduleValidation({
					...withHistory,
					edges: [...withHistory.edges, edge],
					isDirty: true,
				});
			});
		},

		removeEdge: (edgeId: string) => {
			update((state) => {
				const withHistory = pushHistory(state);
				return scheduleValidation({
					...withHistory,
					edges: withHistory.edges.filter((edge) => edge.id !== edgeId),
					isDirty: true,
				});
			});
		},

		setValidationError: (nodeId: string, errors: string[]) => {
			update((state) => {
				const newErrors = new Map(state.validationErrors);
				if (errors.length > 0) {
					newErrors.set(nodeId, errors);
				} else {
					newErrors.delete(nodeId);
				}
				return { ...state, validationErrors: newErrors };
			});
		},

		clearValidationErrors: () => {
			update((state) => ({ ...state, validationErrors: new Map() }));
		},

		reset: () => {
			set(initialState);
		},

		markClean: () => {
			update((state) => ({ ...state, isDirty: false }));
		},

		setPropertyChanges: (nodeId: string, changes: any) => {
			update((state) => ({
				...state,
				selectedNodeId: nodeId,
				propertyChanges: changes,
			}));
		},

		clearPropertyChanges: () => {
			update((state) => ({ ...state, propertyChanges: null }));
		},

		setEditorOpen: (isOpen: boolean) => {
			update((state) => {
				if (isOpen && state.selectedNodeId) {
					const node = state.nodes.find((n) => n.id === state.selectedNodeId);
					const snapshot = node ? deepClone(node) : null;
					return {
						...state,
						isEditorOpen: true,
						pendingNode: snapshot,
						originalNodeForRevert: snapshot,
					};
				}
				return {
					...state,
					isEditorOpen: isOpen,
					pendingNode: isOpen ? state.pendingNode : null,
					originalNodeForRevert: null,
				};
			});
		},

		setPendingNode: (node: FlowNode | null) => {
			update((state) => ({ ...state, pendingNode: node }));
		},

		applyPendingNode: () => {
			update((state) => {
				if (!state.pendingNode?.id)
					return {
						...state,
						pendingNode: null,
						originalNodeForRevert: null,
					};
				const base = state.nodes.find((n) => n.id === state.pendingNode!.id);
				if (!base)
					return {
						...state,
						pendingNode: null,
						originalNodeForRevert: null,
					};

				let merged: FlowNode = { ...base, ...state.pendingNode };
				if (!merged.data?.isLabelManuallyOverridden) {
					merged = {
						...merged,
						data: {
							...merged.data,
							displayLabel: generateNodeLabel(merged),
						},
					};
				}
				merged = omitLegacyDragHandle(merged);
				const nodes = state.nodes.map((n) => (n.id === merged.id ? merged : n));
				return applyValidation({
					...state,
					nodes,
					pendingNode: null,
					originalNodeForRevert: null,
					isDirty: true,
				});
			});
		},

		clearPendingNode: () => {
			update((state) => ({
				...state,
				pendingNode: null,
				originalNodeForRevert: null,
			}));
		},

		revertNodeToOriginal: () => {
			update((state) => {
				if (!state.originalNodeForRevert?.id)
					return {
						...state,
						pendingNode: null,
						originalNodeForRevert: null,
					};
				const rev = state.originalNodeForRevert!;
				const nodes = state.nodes.map((n) => (n.id === rev.id ? omitLegacyDragHandle(rev) : n));
				return {
					...state,
					nodes,
					pendingNode: null,
					originalNodeForRevert: null,
					isDirty: true,
				};
			});
		},

		copySelected: () => {
			update((state) => {
				const ids = state.selectedNodeIds.length
					? state.selectedNodeIds
					: state.selectedNodeId
						? [state.selectedNodeId]
						: [];
				if (!ids.length) return state;
				const idSet = new Set(ids);
				const nodesToCopy = state.nodes.filter((n) => idSet.has(n.id));
				const edgesToCopy = state.edges.filter(
					(e) => idSet.has(e.source) && idSet.has(e.target),
				);
				writeFlowSelectionClipboard({
					nodes: nodesToCopy,
					edges: edgesToCopy,
				});
				return {
					...state,
					clipboard: { nodes: nodesToCopy, edges: edgesToCopy },
				};
			});
		},

		cutSelected: () => {
			update((state) => {
				const ids = state.selectedNodeIds.length
					? state.selectedNodeIds
					: state.selectedNodeId
						? [state.selectedNodeId]
						: [];
				if (!ids.length) return state;
				const idSet = new Set(ids);
				const nodesToCopy = state.nodes.filter((n) => idSet.has(n.id));
				const edgesToCopy = state.edges.filter(
					(e) => idSet.has(e.source) && idSet.has(e.target),
				);
				writeFlowSelectionClipboard({
					nodes: nodesToCopy,
					edges: edgesToCopy,
				});
				const withHistory = pushHistory(state);
				return scheduleValidation({
					...withHistory,
					nodes: withHistory.nodes.filter((node) => !idSet.has(node.id)),
					edges: withHistory.edges.filter(
						(edge) => !idSet.has(edge.source) && !idSet.has(edge.target),
					),
					clipboard: { nodes: nodesToCopy, edges: edgesToCopy },
					selectedNodeId: null,
					selectedNodeIds: [],
					isEditorOpen: false,
					isDirty: true,
				});
			});
		},

		paste: () => {
			update((state) => {
				const clipboard = readFlowSelectionClipboard() ?? state.clipboard;
				if (!clipboard || clipboard.nodes.length === 0) return state;
				const withHistory = pushHistory(state);

				const OFFSET = 60;
				const idMap = new Map<string, string>();

				const newNodes: FlowNode[] = clipboard.nodes.map((n) => {
					const newId = generateUniqueNodeId();
					idMap.set(n.id, newId);
					let copy: FlowNode = {
						...n,
						id: newId,
						position: {
							x: n.position.x + OFFSET,
							y: n.position.y + OFFSET,
						},
						selected: false,
					};
					if (!copy.data?.isLabelManuallyOverridden) {
						copy = {
							...copy,
							data: {
								...copy.data,
								displayLabel: generateNodeLabel(copy),
							},
						};
					}
					return omitLegacyDragHandle(copy);
				});

				const newEdges: FlowEdge[] = clipboard.edges.map((e) => ({
					...e,
					id: generateUniqueNodeId().replace(/^node-/, 'e-'),
					source: idMap.get(e.source) ?? e.source,
					target: idMap.get(e.target) ?? e.target,
				}));

				const newIds = newNodes.map((n) => n.id);

				return scheduleValidation({
					...withHistory,
					nodes: [...withHistory.nodes, ...newNodes],
					edges: [...withHistory.edges, ...newEdges],
					selectedNodeId: newIds[newIds.length - 1] ?? null,
					selectedNodeIds: newIds,
					isDirty: true,
				});
			});
		},

		undo: () => {
			update((state) => {
				if (state.historyIndex < 0) return state;
				const snapshot = ringGet(state._history, state.historyIndex);
				if (!snapshot) return state;
				return {
					...state,
					nodes: snapshot.nodes.map(omitLegacyDragHandle),
					edges: snapshot.edges,
					historyIndex: state.historyIndex - 1,
					isDirty: true,
				};
			});
		},

		redo: () => {
			update((state) => {
				const nextIndex = state.historyIndex + 1;
				if (nextIndex >= state._history.size) return state;
				const snapshot = ringGet(state._history, nextIndex);
				if (!snapshot) return state;
				return {
					...state,
					nodes: snapshot.nodes.map(omitLegacyDragHandle),
					edges: snapshot.edges,
					historyIndex: nextIndex,
					isDirty: true,
				};
			});
		},

		requestLayout: () => {
			if (
				typeof window !== 'undefined' &&
				!window.confirm(
					'Auto-arrange will reposition all nodes using automatic layout. Your manual positions and routing will be replaced. Continue?',
				)
			) {
				return;
			}
			layoutTrigger.update((v) => v + 1);
		},

		setNodesAndEdges: (
			nodes: FlowNode[],
			edges: FlowEdge[],
			opts?: { markClean?: boolean; markDirty?: boolean },
		) => {
			update((state) => {
				const results = validateAllNodes(nodes, edges);
				const nodesWithValidation = nodes.map((n) => {
					const r = results.get(n.id);
					return omitLegacyDragHandle({
						...n,
						validated: r?.isValid ?? true,
						errors: r?.errors ?? [],
					});
				});
				let isDirty = state.isDirty;
				if (opts?.markClean) isDirty = false;
				else if (opts?.markDirty) isDirty = true;
				return { ...state, nodes: nodesWithValidation, edges, isDirty };
			});
		},

		validateNodes: () => {
			update((state) => applyValidation(state));
		},
	};
}

export const flowEditorState = createFlowEditorStore();

/** FlowCanvas registers this so save can flush node positions from the live XYFlow store (mid-drag safe). */
let flushCanvasPositionsToStore: (() => void) | null = null;
/** FlowCanvas registers this to push store nodes/edges into XYFlow immediately after mutations. */
let flushCanvasSyncToStore: (() => void) | null = null;

export function registerFlowCanvasPositionFlush(fn: (() => void) | null) {
	flushCanvasPositionsToStore = fn;
}

export function registerFlowCanvasSync(fn: (() => void) | null) {
	flushCanvasSyncToStore = fn;
}

/** Call before graphToScene on save so `_meta.flowGraph`/scene carry latest x/y from the canvas. */
export function flushLiveFlowPositions() {
	flushCanvasPositionsToStore?.();
}

/** Push latest flowEditorState into the live XYFlow writables (avoids one-frame lag after add/drag). */
export function flushLiveFlowCanvasSync() {
	flushCanvasSyncToStore?.();
}

// Edge midpoint action bus — written by MidpointEdge, read by FlowCanvas
export type EdgeAction =
	| { kind: 'delete'; edgeId: string }
	| {
			kind: 'requestInsert';
			edgeId: string;
			x: number;
			y: number;
	  }
	| { kind: 'reconnectTarget'; edgeId: string; target: string; targetHandle: string | null };

export const edgeActionBus = writable<EdgeAction | null>(null);

// Layout trigger — when set, FlowCanvas runs branch-aware auto-layout
export const layoutTrigger = writable<number>(0);

export const selectedNode = derived(
	flowEditorState,
	($state) => $state.nodes.find((node) => node.id === $state.selectedNodeId) || null,
);

export const hasValidationErrors = derived(
	flowEditorState,
	($state) => $state.validationErrors.size > 0,
);

export const canUndo = derived(flowEditorState, ($state) => $state.historyIndex >= 0);

export const canRedo = derived(
	flowEditorState,
	($state) => $state.historyIndex < $state._history.size - 1 && $state._history.size > 0,
);
